import React, { useState } from "react";
import {
  SwipeableDrawer,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChairIcon from "@mui/icons-material/Chair";

const SeatDrawer = ({
  open,
  onClose,
  onOpen,
  seatDynamicData = [],
  passengerCount = 1,
  onSeatSelection,
  travelerDetails = [],
  onDone
}) => {
  const [selectedSeats, setSelectedSeats] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);

  const toggleSeatSelection = (segmentIndex, rowIndex, seat) => {
    setSelectedSeats((prev) => {
      const seatKey = `${segmentIndex}-${rowIndex}-${seat.Code}`;
      const currentSegment = prev[segmentIndex] || [];
      const isSelected = currentSegment.some((s) => s.Code === seat.Code);
      const allSelectedSeats = Object.values(prev).flat();

      let newSelectedSeats = { ...prev };

      const totalSelectedSeatsInSegment = currentSegment.length;

      if (isSelected) {
        const updatedSegment = currentSegment.filter((s) => s.Code !== seat.Code);
        if (updatedSegment.length === 0) {
          delete newSelectedSeats[segmentIndex];
        } else {
          newSelectedSeats[segmentIndex] = updatedSegment;
        }
      } else {
        const isSeatAlreadySelected = allSelectedSeats.some((s) => s.Code === seat.Code);

        if (isSeatAlreadySelected) {
          return prev;
        }

        if (totalSelectedSeatsInSegment >= passengerCount) {
          return prev;
        }

        const seatDetails = {
          Price: seat.Price || 0,
          Code: seat.Code,
          Origin: seat.Origin,
          Destination: seat.Destination,
        };

        const updatedSegment = [...currentSegment, seatDetails];
        newSelectedSeats[segmentIndex] = updatedSegment;
      }

      const newTotalPrice = Object.values(newSelectedSeats)
        .flat()
        .reduce((sum, s) => sum + (s.Price || 0), 0);
      setTotalPrice(newTotalPrice);

      onSeatSelection(newSelectedSeats);

      return newSelectedSeats;
    });
  };

  const renderSeats = (seats, segmentIndex, rowIndex) => {
    return seats.map((seat) => (
      <React.Fragment key={seat.Code}>
        <Tooltip
          title={`Price: ₹${seat.Price || 0} | ${seat.Description} | ${seat.SeatType}`}
          arrow
        >
          <Box display="flex" flexDirection="column" alignItems="center" mx={1}>
            <IconButton
              onClick={() =>
                seat.AvailablityType !== 3 && toggleSeatSelection(segmentIndex, rowIndex, seat)
              }
              disabled={seat.AvailablityType === 3 || seat.AvailablityType === 4 || seat.AvailablityType === 5}
              sx={{
                backgroundColor: selectedSeats[segmentIndex]?.some((s) => s.Code === seat.Code)
                  ? 'green'
                  : seat.AvailablityType === 1
                    ? 'white'
                    : 'gray',
                color: selectedSeats[segmentIndex]?.some((s) => s.Code === seat.Code)
                  ? 'white'
                  : 'black',
                border: '1px solid',
                borderColor: selectedSeats[segmentIndex]?.some((s) => s.Code === seat.Code)
                  ? 'darkgreen'
                  : 'gray',
                cursor:
                  seat.AvailablityType === 3 || seat.AvailablityType === 4 || seat.AvailablityType === 5
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              <ChairIcon />
            </IconButton>
            <Typography variant="caption" fontWeight="bold">
              {seat.Code || 'No Seat'}
            </Typography>
          </Box>
        </Tooltip>
      </React.Fragment>
    ));
  };

  const renderTravelerSeats = (traveler, travelerIndex) => {
    return (
      <Box key={travelerIndex} mb={4}>
        <Typography variant="h6">{`Traveler ${travelerIndex + 1}`}</Typography>
        {seatDynamicData.length === 0 ? (
          <Typography variant="body1" color="textSecondary">
            Seat selection not available.
          </Typography>
        ) : (
          seatDynamicData.map((segment, segmentIndex) => {
            return segment.SegmentSeat?.map((segmentSeat, segSeatIndex) => (
              <Box key={segSeatIndex} mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  {segmentSeat.RowSeats?.[0]?.Seats?.[0]?.Origin} →{' '}
                  {segmentSeat.RowSeats?.[0]?.Seats?.[0]?.Destination}
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  Flight{' '}
                  {segmentSeat.RowSeats?.[0]?.Seats?.[0]?.FlightNumber} |{' '}
                  {segmentSeat.RowSeats?.[0]?.Seats?.[0]?.CraftType}
                </Typography>

                {segmentSeat.RowSeats?.map((row, rowIndex) => {
                  if (row.Seats?.[0]?.RowNo === '0') return null;

                  return (
                    <Box
                      key={`${segSeatIndex}-${rowIndex}`}
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="subtitle2" mr={2} mt={2}>
                        Row: {row.Seats?.[0]?.RowNo}
                      </Typography>

                      <Box display="flex" gap={1.5}>
                        {row.Seats?.map((seat, seatIndex) => {
                          return renderSeats([seat], segmentIndex, rowIndex);
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ));
          })
        )}
      </Box>
    );
  };


  const mergedTravelerData = Array.isArray(travelerDetails)
    ? travelerDetails.map((traveler, index) => {
      const allSeats = Object.values(selectedSeats).flat();
      const seat = allSeats[index] || null;

      return {
        ...traveler,
        seat,
      };
    })
    : [];

  console.log("Merged Traveler Data:", mergedTravelerData);

  const handleDone = () => {
    onClose();
    console.log("seats per traveler", mergedTravelerData);
    if (onDone) {
      onDone(mergedTravelerData);
    }
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: 1300 }}
    >
      <Box sx={{ width: 550, p: 3, mb: '90px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold" fontFamily={'open sans'}>
            Select Seat
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box mt={3}>
          {travelerDetails?.length > 0
            ? travelerDetails.map((traveler, travelerIndex) =>
              renderTravelerSeats(traveler, travelerIndex)
            )
            : null}
        </Box>
      </Box>

      <Box
        sx={{
          background: '#e3e3e3',
          width: '100%',
          height: '10%',
          position: 'fixed',
          bottom: 0,
          padding: '16px',
          display: 'flex',
          justifyContent: 'flex-start',
          gap: '16px',
          alignItems: 'center',
          zIndex: 1300,
        }}
      >
        <Typography variant="h6">Total Price: ₹{totalPrice}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDone}
        >
          Done
        </Button>
      </Box>
    </SwipeableDrawer>
  );
};

export default SeatDrawer;
